import GenericApi from "@/api/genericApi";
import {
  CurrencyQueryKey,
  DefaultFeeQueryKey,
  ExchangeSettingsQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse, ApiError } from "@/types/Api";
import { Currency } from "@/types/Currency";
import { Fee } from "@/types/Fee";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { getUser } from "@/utils/localStorage";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import SettingsCurrency from "./SettingsCurrency";
import { ExchangeSettings } from "@/types/Exchange";
import { isBitramp } from "@/utils/utils";

const AdminCurrescy: React.FC = () => {
  const user = getUser();

  const isAdmin = user?.is_staff;

  const queryClient = useQueryClient();


  const {
    data: currencies,
    isLoading: isLoadingCurrencies,
    error: currencyError,
  } = useQuery<ApiResponse<Currency[]>>({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  const {
    data: exchangeSettings,
  } = useQuery<ApiResponse<ExchangeSettings>>({
    queryFn: async () => GenericApi.get<ExchangeSettings>(ApiRoutes.EXCHANGESETTINGS),
    queryKey: [ExchangeSettingsQueryKey],
  });

  const [enabledDBW, setEnabledDBW] = useState(exchangeSettings?.data?.balance_enabled);

  const updateExchangeSettings = useMutation<
    ApiResponse<ExchangeSettings>,
    ApiError<ValidationErrors>,
    ExchangeSettings
  >({
    mutationFn: (data) =>
      GenericApi.put<ExchangeSettings>(
        `${ApiRoutes.EXCHANGESETTINGS}`,
        {
          ...data,
        }
      ),
    onSuccess: async ({ }) => {
      queryClient.invalidateQueries({
        queryKey: [ExchangeSettingsQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const handleEditExchangeSettings = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    updateExchangeSettings.mutate({
      balance_enabled: !enabledDBW,
      minimum_amount: 1000,
    });
    setEnabledDBW(!enabledDBW);
  };

  const {
    data: fee,
    isLoading: isLoadingFee,
    error: feeError,
    isSuccess: feeSuccess,
  } = useQuery<ApiResponse<Fee>>({
    queryFn: async () => GenericApi.get<Fee>(ApiRoutes.DEFAULT_FEES),
    queryKey: [DefaultFeeQueryKey],
    enabled: isAdmin,
  });

  const [generalFee, setGeneralFee] = React.useState<string>(
    String(fee?.data?.amount_percentage) ?? "0"
  );

  useEffect(() => {
    if (feeSuccess && fee?.data?.amount_percentage) {
      setGeneralFee(Number(fee?.data?.amount_percentage).toFixed(2));
    } else {
      setGeneralFee("0");
    }
  }, [fee?.data?.amount_percentage, feeSuccess]);

  const handleModifyGeneralFee = () => {
    updateGeneralFeeMutation.mutate({
      amount_percentage: Number(generalFee),
    });
  };

  const getVal = (value: number) => {
    const valueAsString = String(value);

    if (valueAsString === "0") {
      return valueAsString;
    }

    if (valueAsString[0] === "0" && valueAsString[1] !== ".") {
      return valueAsString.slice(1);
    }

    return valueAsString;
  };

  const handleInputGeneralFee = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGeneralFee(String(Number(e.target.value)));
  };

  const updateGeneralFeeMutation = useMutation<
    ApiResponse<Fee>,
    ApiError<ValidationErrors>,
    Fee
  >({
    mutationFn: (data) => GenericApi.post<Fee>(ApiRoutes.DEFAULT_FEES, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      await queryClient.invalidateQueries({
        queryKey: [DefaultFeeQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  if (isLoadingCurrencies || isLoadingFee) {
    return <div>Loading...</div>;
  }

  if (currencyError || feeError) {
    return <div>Error: {currencyError?.message || feeError?.message}</div>;
  }

  return (
    <div className="settings__container--white w-full !gap-4 Container pshadow rounded-tl-none">
      <div className="flex items-center space-x-2">
        <button
          onClick={(e) => handleEditExchangeSettings(e)}
          className={`relative inline-flex items-center h-6 rounded-full w-14 px-1 transition-colors duration-200 ${
            isBitramp() ? "bg-[#464646] " : "bg-[#F6F6F6] border-[0.3px] border-[#D0D0D0]"
          }`}
        >
          <span
            className={`absolute text-[10px] font-semibold ${
              isBitramp() ? enabledDBW ? "left-2 text-[#9F9F9F] !font-[700]" : "right-2 text-[#9F9F9F] !font-[700]" : enabledDBW ? "left-1 text-[#202A43]" : "right-1 text-[#D0D0D0]"
            }`}
          >
            {enabledDBW ? "ON" : "OFF"}
          </span>
          <span
            className={`inline-block w-5 h-5 transform  rounded-full transition-transform duration-200 ${
             isBitramp() ? enabledDBW ? "translate-x-6 bg-[#212121]": "translate-x-0 bg-[#212121]" : enabledDBW ? "translate-x-6 bg-[#202A43]" : "translate-x-0 bg-[#D0D0D0]"
            }`}
          />
        </button>
        <span className="Text dark-blue medium">
          Deposit, Balance and withdraw
        </span>
      </div>
      <div className={`border w-full ${isBitramp() ? "!border-[#464646]" : ""}`}></div>
      <div className="fee flex w-full justify-start items-center gap-10">
        <div className={`${isBitramp() ? "pl-[0.5rem]" : ""} flex justify-center items-center Text dark-blue`}>
          <h2>
            General Fee is{" "}
            <span className="Text bold">
              {Number(fee?.data?.amount_percentage ?? 0).toFixed(2)}%
            </span>
          </h2>
        </div>

        <div className="flex justify-center items-center gap-2 Text dark-blue">
          <h2>Modify General Fee</h2>
          <input
            type="number"
            value={getVal(Number(generalFee))}
            defaultValue={fee?.data?.amount_percentage ?? 0}
            onChange={handleInputGeneralFee}
            className="border-[#D4D4D4] bg-[#F9F9F9] text-center w-[4rem] h-[2rem] rounded-[0.625rem] Input"
          />
          <span className="font-bold">%</span>
          <button
            onClick={handleModifyGeneralFee}
            className="Primary_button "
          >
            Set
          </button>
        </div>
      </div>

      <div className={`border w-full ${isBitramp() ? "!border-[#464646]" : ""}`}></div>

      <SettingsCurrency currencies={currencies?.data ?? null} />

      <div className="mb-5"></div>
    </div>
  );
};

export default AdminCurrescy;
